import { useTranslation } from 'react-i18next';
import { IconButton, Typography } from '@mui/material/';
import { ChevronLeft, ChevronRight, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from '@mui/icons-material';
import { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import { addMonths, addYears, startOfMonth, endOfMonth, isAfter, isBefore } from 'date-fns';

import { getLocale } from '@/components/common/translateFunctions';
import { datePicker } from '@/utilities/dateUtils';

import './DateTimeSelect.css';

export function CustomDatePickerHeader(props: PickersCalendarHeaderProps) {
  const { t, i18n } = useTranslation();
  const locale = getLocale(i18n.language);
  const { currentMonth, onMonthChange, minDate, maxDate } = props;
  const today = new Date();

  // In a standard DatePicker, currentMonth is the reference anchor
  const anchor = currentMonth;
  const headerDate = datePicker(anchor, locale, t);
  const upperLimit: Date = maxDate ?? today;
  const lowerLimit: Date = minDate ?? today;

  // Calculate potential next/prev dates (always stepping by 1 for single picker)
  const nextMonthDate = addMonths(anchor, 1);
  const prevMonthDate = addMonths(anchor, -1);
  const nextYearDate = addYears(anchor, 1);
  const prevYearDate = addYears(anchor, -1);

  // Check validity against limits
  const disableNextMonth = !!upperLimit && isAfter(startOfMonth(nextMonthDate), endOfMonth(upperLimit));
  const disablePrevMonth = !!lowerLimit && isBefore(endOfMonth(prevMonthDate), startOfMonth(lowerLimit));
  const disableNextYear = !!upperLimit && isAfter(startOfMonth(nextYearDate), endOfMonth(upperLimit));
  const disablePrevYear = !!lowerLimit && isBefore(endOfMonth(prevYearDate), startOfMonth(lowerLimit));

  function selectNextMonth() {
    if (!disableNextMonth) {
      onMonthChange(nextMonthDate);
    }
  }

  function selectNextYear() {
    if (!disableNextYear) {
      onMonthChange(nextYearDate);
    }
  }

  function selectPreviousMonth() {
    if (!disablePrevMonth) {
      onMonthChange(prevMonthDate);
    }
  }

  function selectPreviousYear() {
    if (!disablePrevYear) {
      onMonthChange(prevYearDate);
    }
  }

  return (
    <div className="custom-calendar-header">
      <div>
        <IconButton
          onClick={selectPreviousYear}
          title={t('general.dateTime.previousYear')}
          disabled={disablePrevYear}
          size="small"
          data-qa-element-id="calendar_previous_year_button"
        >
          <KeyboardDoubleArrowLeft fontSize="small" />
        </IconButton>
        <IconButton
          onClick={selectPreviousMonth}
          title={t('general.dateTime.previousMonth')}
          disabled={disablePrevMonth}
          size="small"
          data-qa-element-id="calendar_previous_month_button"
        >
          <ChevronLeft fontSize="small" />
        </IconButton>
      </div>

      <Typography variant="subtitle1">{headerDate}</Typography>

      <div>
        <IconButton
          onClick={selectNextMonth}
          title={t('general.dateTime.nextMonth')}
          disabled={disableNextMonth}
          size="small"
          data-qa-element-id="calendar_next_month_button"
        >
          <ChevronRight fontSize="small" />
        </IconButton>
        <IconButton
          onClick={selectNextYear}
          title={t('general.dateTime.nextYear')}
          disabled={disableNextYear}
          size="small"
          data-qa-element-id="calendar_next_year_button"
        >
          <KeyboardDoubleArrowRight fontSize="small" />
        </IconButton>
      </div>
    </div>
  );
}
